import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ViewChild,
  signal,
} from '@angular/core';
import { form } from '@angular/forms/signals';
import { Router } from '@angular/router';

import { StepperComponent, StepperStep } from '@progress/kendo-angular-layout';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';

import { SecurityService } from '@ups/security';
import { SecurityConstants } from '@ups/security-constants';
import {
  HelpActions,
  HelpDocumentDto,
  HelpState,
  HelpTypeEnum,
} from '@ups/xplat/base/in-app-help';
import { BaseComponent, environment } from '@ups/xplat/core';
import { deepClone } from '@ups/xplat/utils';

import { EditorTooltipComponent } from './editor-tooltip/editor-tooltip.component';
import { EditorWalkthroughComponent } from './editor-walkthrough/editor-walkthrough.component';
import { WindowComponent } from '@progress/kendo-angular-dialog';

@Component({
  selector: 'ups-help-editor',
  templateUrl: './editor.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EditorComponent extends BaseComponent {
  editedDoc: HelpDocumentDto = this.getBlankHelpDocument();
  displayEditor = false;

  @ViewChild('stepper') stepper: StepperComponent;
  @ViewChild('tooltipEditor') tooltipEditor: EditorTooltipComponent;
  @ViewChild('walkEditor') walkEditor: EditorWalkthroughComponent;
  @ViewChild('editor') editor: WindowComponent;

  currentStep = 0;

  helpTypeEnum = HelpTypeEnum;

  get canDeleteHelpDocs() {
    return this.security.getFeatureById(
      SecurityConstants.employee_portal_frameworkfeatures_walkthroughs
    ).editAll;
  }

  private securityConstants = SecurityConstants;

  steps: StepperStep[] = [
    {
      label: 'Type',
      icon: 'module-manager',
      isValid: () => this.editedDoc.type !== null,
    },
    {
      label: 'Build',
      icon: 'style-builder',
      isValid: () =>
        this.editedDoc.type === HelpTypeEnum.Tooltip
          ? this.tooltipEditor.f().valid()
          : true,
    },
    {
      label: 'Settings',
      icon: 'settings',
    },
  ];

  saveInProgress = false;

  /**
   * WI 35852 S3b: the Settings step (start page, published) as a signal form. It is
   * seeded from `editedDoc` whenever the store hands the editor a document, and
   * written back onto it just before save.
   */
  readonly settings = signal<{ route: string | null; isPublished: boolean }>({
    route: null,
    isPublished: false,
  });
  readonly settingsForm = form(this.settings);

  constructor(
    private store: Store,
    private actions$: Actions,
    private security: SecurityService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    super();

    store.pipe(HelpState.selectEditorState(this.destroy$)).subscribe((x) => {
      this.displayEditor = x.show;
      this.editedDoc = x.document
        ? deepClone(x.document)
        : this.getBlankHelpDocument();
      this.seedSettings();
      // OnPush: the store emits outside any template event.
      this.cdr.markForCheck();
      if (this.editedDoc.type) {
        this.goToStep(1);
      }
    });

    // interval(500).pipe(
    //   filter(() => this.displayEditor),
    //   takeUntil(this.destroy$),
    // ).subscribe(x => {
    //   console.log(x, this.editor);
    //   this.editor?.bringToFront()
    // });
  }

  hideEditor() {
    this.store.dispatch(HelpActions.toggleEditor({ state: false }));
    this.goToStep(0);
  }

  goToStep(idx: number) {
    console.log(`Current: ${this.currentStep}, Target: ${idx}`);
    for (let index = 0; index < idx; index++) {
      const step = this.steps[index];
      if (typeof step.isValid === 'function') {
        if (!step.isValid(idx)) {
          idx = this.currentStep;
        }
      } else if (step.isValid !== undefined && !step.isValid) {
        idx = this.currentStep;
      }
    }
    this.currentStep = idx;
    if (this.stepper) this.stepper.currentStep = idx;
  }

  saveHelpDoc() {
    this.applySettings();
    this.saveInProgress = true;
    this.store.dispatch(HelpActions.saveHelpDoc({ doc: this.editedDoc }));
    this.actions$
      .pipe(
        ofType(HelpActions.saveHelpDocSuccess, HelpActions.saveHelpDocError),
        take(1)
      )
      .subscribe(() => {
        this.reset();
      });
  }

  getBlankHelpDocument(): HelpDocumentDto {
    return {
      path: '',
      reusable: false,
      // A blank doc starts untitled and untyped; the editor fills both before save.
      title: null!,
      type: null!,
      applicationGuid: environment.security!.appId,
      isPublished: false,
      // Default a new tour's home route to the page the author is building it
      // on, so the player lands users there before step 0. `?.` guards the
      // field initializer (line ~30), which runs before the router is injected;
      // the subscription/reset re-call this with the router available.
      route: this.router?.url,
    };
  }

  /** Settings-step helper: stamp the editor's current page as the tour route. */
  useCurrentPageAsRoute(): void {
    this.settings.update((s) => ({ ...s, route: this.router.url }));
  }

  private seedSettings(): void {
    this.settings.set({
      route: this.editedDoc.route ?? null,
      isPublished: !!this.editedDoc.isPublished,
    });
  }

  /** Writes the Settings step onto the document. A null route was never typed, so
   * the document keeps whatever it had (including no key at all). */
  private applySettings(): void {
    const { route, isPublished } = this.settings();
    if (route !== null) {
      this.editedDoc.route = route;
    }
    this.editedDoc.isPublished = isPublished;
  }

  deleteHelpDoc(): void {
    const shouldDelete = window.confirm(
      `Delete this ${
        this.editedDoc.type === HelpTypeEnum.Tooltip ? 'tooltip' : 'walkthrough'
      }?`
    );
    if (!shouldDelete) {
      return;
    }

    this.saveInProgress = true;
    this.store.dispatch(
      HelpActions.deleteDocument({ document: this.editedDoc })
    );
    this.actions$
      .pipe(
        ofType(
          HelpActions.deleteDocumentSuccess,
          HelpActions.deleteDocumentError
        ),
        take(1)
      )
      .subscribe(() => {
        this.reset();
      });
  }

  reset() {
    // OnPush: only reached from the save/delete action callbacks, which fire
    // outside any template event.
    this.cdr.markForCheck();
    this.goToStep(0);
    this.editedDoc = this.getBlankHelpDocument();
    this.seedSettings();
    this.saveInProgress = false;
  }
}
